/**
 * HomePage — the dual-core homepage (v1.1 双核心打磨清单 A1).
 *
 * v1.1 scope decision (2026-09-08): the MVP doubles down on Layer 1 (SOS
 * first-aid) and Layer 2 (AI empathetic chat). Daily recording, timeline and
 * insights are deferred — their entries are removed from the homepage (code
 * kept, routes sealed; see A2).
 *
 * Layout (top to bottom):
 *   - theme toggle (top-right)
 *   - MindSpace logo + calm greeting
 *   - PRIMARY CTA "现在很难受？立即急救" → /sos/emotion (visual focus)
 *   - secondary entry "想聊一聊" → /chat
 */

import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useThemeStore } from '../store/themeStore';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useThemeStore();

  return (
    <div
      className="flex flex-col items-center justify-center min-h-screen px-6 py-10 relative transition-colors"
      style={{
        backgroundColor: 'var(--bg-primary)',
        color: 'var(--text-primary)',
      }}
    >
      {/* theme toggle (top-right) */}
      <motion.button
        onClick={toggleTheme}
        aria-label="切换主题"
        className="absolute top-6 right-6 p-3 rounded-full transition-all hover:scale-105"
        style={{ backgroundColor: 'var(--bg-secondary)' }}
        whileTap={{ scale: 0.95 }}
      >
        {theme === 'light' ? (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
          </svg>
        ) : (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        )}
      </motion.button>

      {/* Logo + calm greeting */}
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="text-center mb-8"
      >
        <div className="w-24 h-24 rounded-3xl flex items-center justify-center mb-6 mx-auto shadow-xl bg-gradient-to-br from-purple-400 to-pink-500">
          <span className="text-3xl text-white font-bold">M</span>
        </div>
        <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
          MindSpace
        </h1>
        <p className="text-base font-medium" style={{ color: 'var(--text-secondary)' }}>
          慢慢来，这里有一刻属于你。
        </p>
      </motion.div>

      <div className="w-full max-w-sm flex flex-col gap-5">
        {/* PRIMARY CTA — SOS first-aid (visual focus) */}
        <motion.button
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          onClick={() => navigate('/sos/emotion')}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="w-full flex flex-col items-center justify-center gap-1 font-semibold text-white rounded-2xl transition-all"
          style={{
            backgroundColor: 'var(--accent)',
            boxShadow: '0 10px 30px -8px var(--accent)',
            padding: '1.5rem 2rem',
            fontSize: '1.25rem',
          }}
        >
          <span>现在很难受？</span>
          <span className="text-sm font-normal opacity-90">60 秒急救，先让自己撑住</span>
          <motion.span
            className="arrow-right"
            animate={{ x: [0, 4, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            →
          </motion.span>
        </motion.button>

        {/* SECONDARY — AI empathetic chat */}
        <motion.button
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          onClick={() => navigate('/chat')}
          whileTap={{ scale: 0.98 }}
          className="w-full flex items-center justify-center gap-2 font-medium rounded-2xl transition-all"
          style={{
            color: 'var(--text-primary)',
            backgroundColor: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)',
            padding: '1rem 1.5rem',
            fontSize: '1rem',
          }}
        >
          想聊一聊 →
        </motion.button>
      </div>
    </div>
  );
};

export default HomePage;
